/**
 * ChatManager.tsx
 * 
 * Manages multiple Facebook-style chat windows:
 * - Tracks open chats
 * - Handles z-index stacking
 * - Manages chat states (open, minimized, expanded)
 * - Persists chat state across page navigation
 * - Shows scrollable header list for minimized chats
 */

import React, { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import ChatWindow from './ChatWindow'
import { useAuth } from '../../contexts/AuthContext'
import { X, MessageSquare } from 'lucide-react'

interface Chatbot {
  id: string
  name: string
  image: string
  description: string
  isFree: boolean
}

interface ChatState {
  chatbot: Chatbot
  isOpen: boolean
  isMinimized: boolean
  isExpanded: boolean
  zIndex: number
}

const ChatManager: React.FC = () => {
  const [chats, setChats] = useState<ChatState[]>([])
  const [nextZIndex, setNextZIndex] = useState(1000)
  const [showHeaderList, setShowHeaderList] = useState(false)
  const { state: authState } = useAuth()
  const location = useLocation()

  // Only show chat manager on business dashboard pages (routes starting with /orgs/)
  const isBusinessDashboardPage = location.pathname.startsWith('/orgs/')

  // Load chat state from localStorage on mount
  useEffect(() => {
    const savedChats = localStorage.getItem('chatWindows')
    if (savedChats) {
      try {
        const parsedChats = JSON.parse(savedChats)
        setChats(parsedChats)
        // Set next z-index to be higher than existing chats
        const maxZIndex = Math.max(...parsedChats.map((chat: ChatState) => chat.zIndex), 999)
        setNextZIndex(maxZIndex + 1)
      } catch (error) {
        console.error('Error loading chat state:', error)
      }
    }
  }, [])

  // Save chat state to localStorage whenever chats change
  useEffect(() => {
    localStorage.setItem('chatWindows', JSON.stringify(chats))
  }, [chats])

  const openChat = (chatbot: Chatbot) => {
    console.log('🔍 ChatManager: openChat called with:', chatbot)
    
    // Check if chat is already open
    const existingChat = chats.find(chat => chat.chatbot.id === chatbot.id)
    if (existingChat) {
      console.log('🔍 ChatManager: Chat already open, bringing to front')
      // Bring to front
      bringToFront(chatbot.id)
      return
    }

    console.log('🔍 ChatManager: Creating new chat for:', chatbot.name)
    const newChat: ChatState = {
      chatbot,
      isOpen: true,
      isMinimized: false,
      isExpanded: false,
      zIndex: nextZIndex
    }

    setChats(prev => {
      const updated = [...prev, newChat]
      console.log('🔍 ChatManager: Updated chats:', updated)
      return updated
    })
    setNextZIndex(prev => prev + 1)
  }

  const closeChat = (chatbotId: string) => {
    setChats(prev => prev.filter(chat => chat.chatbot.id !== chatbotId))
  }

  const minimizeChat = (chatbotId: string) => {
    setChats(prev => prev.map(chat => 
      chat.chatbot.id === chatbotId 
        ? { ...chat, isMinimized: true, isExpanded: false }
        : chat
    ))
  }

  const expandChat = (chatbotId: string) => {
    setChats(prev => prev.map(chat => 
      chat.chatbot.id === chatbotId 
        ? { ...chat, isExpanded: true, isMinimized: false }
        : chat
    ))
  }

  const restoreChat = (chatbotId: string) => {
    setChats(prev => prev.map(chat => 
      chat.chatbot.id === chatbotId 
        ? { ...chat, isMinimized: false, isExpanded: false }
        : chat
    ))
  }

  const bringToFront = (chatbotId: string) => {
    setChats(prev => prev.map(chat => 
      chat.chatbot.id === chatbotId 
        ? { ...chat, zIndex: nextZIndex }
        : chat
    ))
    setNextZIndex(prev => prev + 1)
  }

  const handleChatClick = (chatbotId: string) => {
    bringToFront(chatbotId)
  }

  const handleHeaderListToggle = () => {
    setShowHeaderList(!showHeaderList)
  }

  const handleHeaderClick = (chatbotId: string) => {
    restoreChat(chatbotId)
    bringToFront(chatbotId)
    setShowHeaderList(false)
  }

  const handleHeaderDelete = (chatbotId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    closeChat(chatbotId)
  }

  // Close header list when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element
      if (showHeaderList && !target.closest('.header-list-container')) {
        setShowHeaderList(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showHeaderList])

  // Expose openChat function globally for other components to use
  useEffect(() => {
    (window as any).openChatWindow = openChat
    return () => {
      delete (window as any).openChatWindow
    }
  }, [chats])

  const minimizedChats = chats.filter(chat => chat.isMinimized)

  // Don't render anything if not on a business dashboard page
  if (!isBusinessDashboardPage) {
    return null
  }

  return (
    <>
      {/* Header List Toggle Button - Always visible */}
      <button
        onClick={handleHeaderListToggle}
        className="fixed bottom-4 right-4 z-[9999] bg-primary-500 text-white p-3 rounded-full shadow-lg hover:bg-primary-600 transition-colors header-list-container"
        title="Show minimized chats"
      >
        <MessageSquare className="h-5 w-5" />
        {minimizedChats.length > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
            {minimizedChats.length}
          </span>
        )}
      </button>

      {/* Header List */}
      {showHeaderList && (
        <div className="fixed bottom-20 right-4 z-[9998] bg-white rounded-lg shadow-lg border border-gray-200 max-w-xs header-list-container">
          <div className="p-3 border-b border-gray-200">
            <h3 className="text-sm font-medium text-gray-900">Minimized Chats</h3>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {minimizedChats.length > 0 ? (
              minimizedChats.map((chat) => (
                <div
                  key={chat.chatbot.id}
                  onClick={() => handleHeaderClick(chat.chatbot.id)}
                  className="flex items-center space-x-3 p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                >
                  <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                    <img 
                      src={chat.chatbot.image} 
                      alt={chat.chatbot.name} 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-medium text-gray-900 truncate">
                      {chat.chatbot.name}
                    </h4>
                    <p className="text-xs text-gray-500 truncate">
                      Click to restore
                    </p>
                  </div>
                  <button
                    onClick={(e) => handleHeaderDelete(chat.chatbot.id, e)}
                    className="text-gray-400 hover:text-red-600 transition-colors flex-shrink-0"
                    title="Delete chat"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))
            ) : (
              <div className="p-6 text-center">
                <MessageSquare className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No chats</p>
                <p className="text-xs text-gray-400 mt-1">Start a chat to see it here</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Chat Windows */}
      {chats.map((chat) => (
        <ChatWindow
          key={chat.chatbot.id}
          chatbot={chat.chatbot}
          isOpen={chat.isOpen}
          isMinimized={chat.isMinimized}
          isExpanded={chat.isExpanded}
          onClose={() => closeChat(chat.chatbot.id)}
          onMinimize={() => minimizeChat(chat.chatbot.id)}
          onExpand={() => expandChat(chat.chatbot.id)}
          onRestore={() => restoreChat(chat.chatbot.id)}
          zIndex={chat.zIndex}
        />
      ))}
    </>
  )
}

export default ChatManager 